import { useState } from 'react';
import { Box, Chip, Tab, Tabs, Tooltip } from '@mui/material';
import * as Icon from '@mui/icons-material/';
import { toast } from 'react-toastify';

import ComponentTable from '../../../Components/Table/Index';
import { Column } from '../../../Components/Table/Types';
import { HeaderComponent } from '../../../../shared/components/HeaderComponent';
import FormBuilder from '../../../../shared/components/FormBuilder';
import { Button } from '../../../../shared/components/button/Button';
import { formatCurrency } from '../../../../shared/utils/helpers/numbers';

import DevolucaoFornecedorController from '../../../../controllers/DevolucaoFornecedorController';
import ProdutoController from '../../../../controllers/ProdutoController';
import { ModalCriarDevolucao, ItemParaDevolver } from '../Modal/ModalCriarDevolucao';
import { AbaRegistros } from './AbaRegistros';

/**
 * Tela 1 — Devolução ao fornecedor, consulta por produto.
 *
 * O PRODUTO É O PONTO DE PARTIDA, e não a nota. É assim que a pergunta chega: "esta peça
 * veio com defeito, de qual nota ela saiu e quanto ainda dá para devolver?". Quem tem a
 * peça na mão raramente tem o número da nota.
 *
 * Uma linha por ITEM de nota, não por nota: o mesmo produto pode estar em duas linhas da
 * mesma nota, com preços diferentes, e aí são dois saldos — o valor que a devolução
 * espelha é o do item.
 *
 * A REGRA VEM PRONTA DO BANCO. `pode_devolver` e `motivo_bloqueio` são calculados na
 * procedure; aqui só se desabilita o botão e se mostra o motivo ao lado. Botão apagado
 * sem explicação não ajuda ninguém — e "esta nota foi lançada sem XML" é um caso que o
 * operador não tem como adivinhar sozinho.
 */
interface Linha {
  nfeIt_id: number;
  nfe_id: number;
  numero_nota: number;
  serie?: string;
  cli_id: number;
  fornecedor?: string;
  data_emissao: string;
  dias: number;
  prod_id: number;
  codigo_promatec?: string;
  produto?: string;
  qtd_na_nota: number;
  qtd_devolvida: number;
  saldo: number;
  valor_unitario: number;
  tem_chave: string;
  alerta_prazo: string;
  pode_devolver: string;
  motivo_bloqueio?: string;
}

export default function DevolucaoFornecedorPesquisar() {
  /* Duas abas e nao duas rotas: sao dois passos do MESMO assunto, e o operador vai e
     volta entre eles - registra uma devolucao, confere a lista, registra outra. Duas
     rotas custariam um item de menu a mais, uma permissao a mais e duas telas para
     procurar a mesma coisa. */
  const [aba, setAba] = useState(0);

  const [produtos, setProdutos] = useState<any[]>([]);
  const [linhas, setLinhas] = useState<any[]>([]);
  const [consultou, setConsultou] = useState(false);

  const [itemEscolhido, setItemEscolhido] = useState<ItemParaDevolver | null>(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Guardado para repetir a consulta depois de gravar, sem o operador filtrar de novo.
  const [ultimaBusca, setUltimaBusca] = useState<any>(null);

  const tableColumns: Column[] = [
    { id: 'Nota', label: 'Nota', minWidth: 0, maxWidth: 90 },
    { id: 'Fornecedor', label: 'Fornecedor', minWidth: 160, maxWidth: 320 },
    { id: 'Emissao', label: 'Emissão', minWidth: 0, maxWidth: 110 },
    { id: 'Dias', label: 'Dias', minWidth: 0, maxWidth: 70, align: 'right' },
    { id: 'Produto', label: 'Produto', minWidth: 160, maxWidth: 320 },
    { id: 'QtdNota', label: 'Qtd. na nota', minWidth: 0, maxWidth: 110, align: 'right' },
    { id: 'QtdDevolvida', label: 'Já devolvida', minWidth: 0, maxWidth: 110, align: 'right' },
    { id: 'Saldo', label: 'Saldo', minWidth: 0, maxWidth: 90, align: 'right' },
    { id: 'Valor', label: 'Valor unit.', minWidth: 0, maxWidth: 110, align: 'right' },
    { id: 'Acao', label: '', minWidth: 0, maxWidth: 170, align: 'right' },
  ];

  const fields = [
    {
      name: 'produto',
      type: 'autocomplete',
      label: 'Produto',
      options: produtos,
      optionLabel: 'label',
      optionKey: 'id',
      col: 5,
      onInput: (_e: any, v: string) => buscarProdutos(v),
    },
    { name: 'DataInicio', type: 'date', label: 'Emissão de', col: 2 },
    { name: 'DataFim', type: 'date', label: 'até', col: 2 },
    { type: 'submit', icon: <Icon.Search style={{ fontSize: '28px' }} /> },
  ];

  const initialValues = { produto: null, DataInicio: '', DataFim: '' };

  const buscarProdutos = async (texto: string) => {
    if (!texto || texto.length < 2) return;

    const response = await ProdutoController.Listar({
      Descricao: texto,
      CodigoBarras: '',
      Tipo: '',
      PartNumber: '',
      Grupo: '',
    } as any);

    if (response.StatusCode === 200 && Array.isArray(response.Data)) {
      setProdutos(
        response.Data.map((item: any) => ({
          id: item.prod_id,
          label: `${item.prodID_promatec ?? ''} - ${item.prod_descricao ?? ''}`,
          ...item,
        }))
      );
    }
  };

  const montarLinha = (item: Linha) => ({
    Nota: `${item.numero_nota}${item.serie ? ` / ${String(item.serie).trim()}` : ''}`,
    Fornecedor: item.fornecedor ?? '(não identificado)',
    Emissao: item.data_emissao ? new Date(item.data_emissao).toLocaleDateString('pt-BR') : '',
    Dias:
      item.alerta_prazo === 'S' ? (
        <Tooltip title="Mais de 45 dias desde a emissão. O prazo é condição para o crédito do imposto — não impede devolver.">
          <Chip size="small" color="warning" label={item.dias} />
        </Tooltip>
      ) : (
        item.dias
      ),
    Produto: `${item.codigo_promatec ?? ''} - ${item.produto ?? ''}`,
    QtdNota: item.qtd_na_nota,
    QtdDevolvida: item.qtd_devolvida,
    Saldo: item.saldo,
    Valor: formatCurrency(item.valor_unitario),
    Acao:
      item.pode_devolver === 'S' ? (
        <Button
          className=""
          variant="success"
          title="Devolver"
          iconStart={<Icon.Undo />}
          onClick={() => abrirModal(item)}
        />
      ) : (
        /* O motivo fica VISÍVEL, não escondido num tooltip de ícone desabilitado: é ele
           que diz ao operador o que fazer — mandar o XML ao suporte, no caso da nota sem
           chave. */
        <Tooltip title={item.motivo_bloqueio ?? ''}>
          <Chip
            size="small"
            color="default"
            icon={<Icon.Block style={{ fontSize: '16px' }} />}
            label={item.tem_chave === 'N' ? 'Sem chave' : 'Sem saldo'}
          />
        </Tooltip>
      ),
  });

  const consultar = async (values: any) => {
    const prodId = values?.produto?.id ?? values?.produto?.prod_id;

    if (!prodId) {
      toast('Escolha um produto para consultar.', { type: 'warning' });
      return;
    }

    const request = {
      ProdId: prodId,
      DataInicio: values.DataInicio || null,
      DataFim: values.DataFim || null,
      CliId: null,
      DiasAlerta: 45,
    };

    const response = await DevolucaoFornecedorController.Consultar(request);
    setConsultou(true);
    setUltimaBusca(request);

    if (response.StatusCode === 200 && Array.isArray(response.Data)) {
      setLinhas(response.Data.map(montarLinha));

      if (response.Data.length === 0) {
        toast('Nenhuma nota de entrada finalizada com este produto.', { type: 'warning' });
      }
      return;
    }

    if (response.StatusCode === 204) {
      setLinhas([]);
      toast('Nenhuma nota de entrada finalizada com este produto.', { type: 'warning' });
      return;
    }

    setLinhas([]);
    toast(response.ErroDetalhado ?? 'Não foi possível consultar as notas deste produto.', {
      type: 'error',
    });
  };

  const repetirUltimaBusca = async () => {
    if (!ultimaBusca) return;
    const response = await DevolucaoFornecedorController.Consultar(ultimaBusca);
    if (response.StatusCode === 200 && Array.isArray(response.Data)) {
      setLinhas(response.Data.map(montarLinha));
    }
  };

  const abrirModal = (item: Linha) => {
    setItemEscolhido({
      nfeIt_id: item.nfeIt_id,
      numero_nota: item.numero_nota,
      serie: item.serie,
      fornecedor: item.fornecedor,
      produto: item.produto,
      codigo_promatec: item.codigo_promatec,
      qtd_na_nota: item.qtd_na_nota,
      qtd_devolvida: item.qtd_devolvida,
      saldo: item.saldo,
      valor_unitario: item.valor_unitario,
      dias: item.dias,
      alerta_prazo: item.alerta_prazo,
    });
    setModalAberto(true);
  };

  const confirmarDevolucao = async (quantidade: number, motivo: string) => {
    if (!itemEscolhido) return;

    setSalvando(true);
    const response = await DevolucaoFornecedorController.Gravar({
      NfeItId: itemEscolhido.nfeIt_id,
      Quantidade: quantidade,
      Motivo: motivo || null,
      Data: null,
    });
    setSalvando(false);

    if (response.StatusCode === 200) {
      const saldo = response.Data?.saldo_restante;
      toast(
        `Devolução criada. Saldo devolvível deste item: ${saldo ?? '—'}.`,
        { type: 'success' }
      );
      setModalAberto(false);
      // A linha na grade precisa refletir o saldo novo; sem isso o operador veria o
      // saldo antigo e poderia tentar devolver de novo o que acabou de reservar.
      await repetirUltimaBusca();
      return;
    }

    toast(response.ErroDetalhado ?? 'Não foi possível criar a devolução.', { type: 'error' });
  };

  return (
    <Box>
      <HeaderComponent text={'DEVOLUÇÃO AO FORNECEDOR'} />

      <Tabs value={aba} onChange={(_, v) => setAba(v)} sx={{ mb: 1 }}>
        <Tab label="Consultar por produto" />
        <Tab label="Devoluções registradas" />
      </Tabs>

      {aba === 1 && <AbaRegistros />}

      <Box sx={{ display: aba === 0 ? 'block' : 'none' }}>
      <FormBuilder fields={fields} initialValues={initialValues} onSubmit={consultar} />

      <ComponentTable columns={tableColumns} rows={linhas} />

      {consultou && linhas.length === 0 && (
        <Box sx={{ p: 2, color: 'text.secondary', fontSize: 14 }}>
          Nenhuma nota de entrada <strong>finalizada</strong> com este produto no período.
        </Box>
      )}

      </Box>

      <ModalCriarDevolucao
        aberto={modalAberto}
        item={itemEscolhido}
        salvando={salvando}
        aoFechar={() => setModalAberto(false)}
        aoConfirmar={confirmarDevolucao}
      />
    </Box>
  );
}
